import { useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Container } from 'typescript-ioc/es5'
import { notification } from 'antd'
import { GetNotificationScheduleResponse } from '@pushly/models/lib/structs/notifications/get-requests/get-notification-schedule-response'
import { NotificationScheduleService } from '../../services/notification-schedule'
import { confirmDestructiveAction } from '../utils/confirm-destructive-action'
import { mobileRoutes } from '../utils/mobile-routes'

interface UseNotificationActionsOptions {
    /** Domain ID for route construction */
    domainId: string | number | null | undefined
    /** Notification ID for route construction and API calls */
    notificationId: string | number | null | undefined
    /** Title of the notification (used in confirmation modals) */
    title?: string
    /** Schedule ID for cancel API call (falls back to notificationId) */
    scheduleId?: string | number | null
    /** Called before navigation actions (e.g., to close a sheet) */
    onBeforeNavigate?: () => void
    /** Called after a successful mutation (cancel/archive) */
    onChange?: () => void
    /** Called after successful cancel (e.g., to navigate back) */
    onAfterCancel?: () => void
}

interface UseNotificationActionsResult {
    handleEdit: () => void
    handleDuplicate: () => void
    handleCancel: () => void
    handleArchive: (archiveFn: (notificationId: number) => void | Promise<void>) => void
}

/**
 * Shared notification action handlers for edit, duplicate, cancel, and archive.
 * Eliminates duplication across notification-card, quick-sheet, and detail page.
 */
export function useNotificationActions({
    domainId,
    notificationId,
    title,
    scheduleId,
    onBeforeNavigate,
    onChange,
    onAfterCancel,
}: UseNotificationActionsOptions): UseNotificationActionsResult {
    const history = useHistory()
    const scheduleService = useRef(Container.get(NotificationScheduleService)).current

    const handleEdit = useCallback(() => {
        onBeforeNavigate?.()
        if (domainId && notificationId) {
            history.push(mobileRoutes.notificationEdit(domainId, notificationId))
        }
    }, [domainId, notificationId, history, onBeforeNavigate])

    const handleDuplicate = useCallback(() => {
        onBeforeNavigate?.()
        if (domainId && notificationId) {
            history.push(mobileRoutes.notificationDuplicate(domainId, notificationId))
        }
    }, [domainId, notificationId, history, onBeforeNavigate])

    const handleCancel = useCallback(() => {
        if (!notificationId) return
        onBeforeNavigate?.()
        confirmDestructiveAction({
            title: 'Cancel Notification',
            entityTitle: title,
            message: 'This notification{entity} will be cancelled. This cannot be undone.',
            okText: 'Cancel Notification',
            onConfirm: async () => {
                try {
                    const id = scheduleId ?? notificationId
                    const schedule = GetNotificationScheduleResponse.parseJsonObject({ id: String(id) })
                    await scheduleService.cancelSchedule(schedule, `mv.notif-actions.cancel#${notificationId}`)
                    notification.success({ message: 'Notification cancelled' })
                    onAfterCancel?.()
                    onChange?.()
                } catch {
                    notification.error({ message: 'Failed to cancel notification' })
                    onChange?.()
                }
            },
        })
    }, [notificationId, title, scheduleId, scheduleService, onBeforeNavigate, onChange, onAfterCancel])

    const handleArchive = useCallback(
        (archiveFn: (notificationId: number) => void | Promise<void>) => {
            if (!notificationId) return
            onBeforeNavigate?.()
            confirmDestructiveAction({
                title: 'Archive Draft',
                entityTitle: title,
                message: 'This draft{entity} will be archived and no longer appear in your drafts list.',
                okText: 'Archive',
                onConfirm: () => {
                    archiveFn(Number(notificationId))
                },
            })
        },
        [notificationId, title, onBeforeNavigate],
    )

    return { handleEdit, handleDuplicate, handleCancel, handleArchive }
}
